
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import logo from '@/assets/logo_EMA.png';
import { LanguageSwitcher } from '@/components/Layout/LanguageSwitcher';

const PreAuth: React.FC = () => {
  const { t } = useTranslation();
  const [developmentName, setDevelopmentName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const typewriterRef = useRef<HTMLSpanElement>(null);

  const typewriterText = t('auth.welcomeToEma');

  useEffect(() => {
    let index = 0;
    const interval = setInterval(() => {
      if (index < typewriterText.length) {
        if (typewriterRef.current) {
          typewriterRef.current.textContent = typewriterText.slice(0, index + 1);
        }
        index++;
      } else {
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [typewriterText]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // LoginForm re-hydrates its error from this sessionStorage key so a page
    // refresh mid-login doesn't lose it -- but that means a stale error from
    // an earlier failed login attempt (e.g. "account deactivated") would
    // otherwise resurface the instant you land back on /login, even though
    // this is a fresh attempt with a newly-resolved organization. Starting a
    // new PreAuth submission supersedes any previous login attempt, so clear it.
    sessionStorage.removeItem('login_error_temp');

    const typed = developmentName.trim();
    if (!typed) {
      setError(t('auth.enterOrgId'));
      return;
    }

    // Special case: redirect to onboarding
    if (typed.toLowerCase() === 'onboarding') {
      navigate('/onboarding');
      return;
    }

    setLoading(true);

    try {
      // Try the typed text as a development/site name first (public.locations).
      // Fall back to treating it as an organization name -- today's behavior --
      // so an org with no locations set up yet (e.g. straight after onboarding)
      // can still log in.
      const { data: locationRow, error: locationError } = await supabase
        .rpc('get_public_location', { p_name: typed })
        .maybeSingle();

      if (locationError) {
        console.error('Location lookup error:', locationError);
      }

      const orgId: string | null = locationRow?.organization_id ?? null;
      const matchedLocationId: string | null = locationRow?.id ?? null;

      if (orgId) {
        // Resolved via a location -- get_public_location already joins the
        // org's name/status, so this is the only call needed. Also
        // pre-select the site so the header's LocationSwitcher lands on it
        // instead of "All Zanzibar".
        if (locationRow.organization_status !== 'active') {
          setError(t('auth.orgInactive'));
          setLoading(false);
          return;
        }

        localStorage.setItem('organizationId', orgId);
        localStorage.setItem('organizationName', locationRow.organization_name);
        if (matchedLocationId) {
          localStorage.setItem('sme_selected_location', matchedLocationId);
        }
        navigate('/login');
        return;
      }

      const { data: orgRow, error: orgError } = await supabase
        .rpc('get_public_organization', { p_name: typed })
        .maybeSingle();

      console.log('Organization lookup result:', { orgRow, orgError });

      if (orgError) {
        console.error('Database error:', orgError);
        if (typed.toLowerCase() === 'fumbademo') {
          localStorage.setItem('organizationId', '00000000-0000-0000-0000-000000000001');
          localStorage.setItem('organizationName', 'fumbademo');
          navigate('/login');
          return;
        }
        setError(t('auth.unableToVerifyOrg'));
        setLoading(false);
        return;
      }

      if (!orgRow) {
        if (typed.toLowerCase() === 'fumbademo') {
          localStorage.setItem('organizationId', '00000000-0000-0000-0000-000000000001');
          localStorage.setItem('organizationName', 'fumbademo');
          navigate('/login');
          return;
        }
        setError(t('auth.orgNotFound'));
        setLoading(false);
        return;
      }

      if (orgRow.status !== 'active') {
        setError(t('auth.orgInactive'));
        setLoading(false);
        return;
      }

      localStorage.setItem('organizationId', orgRow.id);
      localStorage.setItem('organizationName', orgRow.name);
      navigate('/login');
    } catch (error: any) {
      console.error('Error validating organization:', error);
      // Try to proceed anyway if it's just fumbademo (hardcoded fallback for network issues)
      if (typed.toLowerCase() === 'fumbademo') {
        localStorage.setItem('organizationId', '00000000-0000-0000-0000-000000000001');
        localStorage.setItem('organizationName', 'fumbademo');
        navigate('/login');
        return;
      }
      const errorMessage = error?.message?.includes('Failed to fetch')
        ? t('auth.networkError')
        : t('auth.unableToVerifyOrg');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-primary/5 to-secondary/10 dark:from-background dark:via-primary/10 dark:to-secondary/5 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8">
        {/* Language switcher */}
        <div className="flex justify-end">
          <LanguageSwitcher />
        </div>
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <div className="relative">
              <img
                src={logo}
                alt="EMA Logo"
                className="w-16 h-16"
              />
            </div>
          </div>
          
          {/* Typewriter Effect */}
          <div className="h-16 flex items-center justify-center">
            <h1 className="text-2xl md:text-3xl font-bold text-white text-center">
              <span ref={typewriterRef}></span>{developmentName ? ` - ${developmentName.split('-')[0]}` : ''}
              <span className="animate-pulse">|</span>
            </h1>
          </div>
          
          <p className="text-blue-100 text-sm">
            {t('auth.enterOrgIdPrompt')}
          </p>
        </div>

        {/* Organization Input Card */}
        <div 
          className="rounded-lg border-0"
          style={{
            background: 'linear-gradient(to right bottom, rgb(0, 0, 0), rgb(17, 24, 39), rgb(0, 0, 0))',
            borderBottomColor: 'rgb(30, 41, 59)',
            borderBottomWidth: '2px',
            borderBottomStyle: 'solid'
          }}
        >
          <div className="p-6">
            <h3 className="text-white text-center text-lg font-semibold mb-6">{t('auth.organizationAccess')}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert className="bg-red-500/10 border-red-500/20 text-white">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <div>
                <Input
                  type="text"
                  placeholder={t('auth.enterOrgName')}
                  value={developmentName}
                  onChange={(e) => setDevelopmentName(e.target.value)}
                  className="bg-white/20 border-white/30 text-white placeholder:text-blue-100 focus:border-white/50"
                  required
                  disabled={loading}
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-white/20 hover:bg-white/30 text-white font-semibold py-3 border border-white/30"
                disabled={!developmentName.trim() || loading}
              >
                {loading ? t('auth.validating') : t('auth.continueToLogin')}
              </Button>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-blue-200 text-xs">
            {t('auth.preAuthPoweredBy')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PreAuth;
